import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, Smartphone, ExternalLink, FileJson, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { WellKnownEditor } from "../../components/common/WellKnownEditor";

/**
 * Super Admin → "Mobile Apps" — one-click PWA bundle download for any
 * tenant on the platform PLUS an in-place editor for the per-tenant
 * `assetlinks.json` and `apple-app-site-association` files we serve at
 * `/.well-known/...` on each tenant's domain.
 */
export default function SuperAdminPwaBundles() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {id, slug, name, ...} of tenant being edited

  useEffect(() => {
    let alive = true;
    api.get("/superadmin/tenants")
      .then(({ data }) => alive && setTenants(Array.isArray(data) ? data : (data.items || data.tenants || [])))
      .catch(() => toast.error("Could not load tenants"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const rows = useMemo(() => {
    return tenants.map((row) => ({
      id: row.tenant_id || row.id,
      slug: row.tenant_slug || row.slug,
      name: row.tenant_name || row.name,
      status: row.tenant_status || row.status,
      custom_domain: row.custom_domain,
    })).filter((r) => r.id && r.slug);
  }, [tenants]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((t) =>
      (t.name || "").toLowerCase().includes(needle) ||
      (t.slug || "").toLowerCase().includes(needle) ||
      (t.custom_domain || "").toLowerCase().includes(needle)
    );
  }, [q, rows]);

  const download = async (t) => {
    setBusyId(t.id);
    try {
      const res = await api.get(
        `/superadmin/tenants/${t.id}/pwa/bundle`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t.slug}-mobile-app-bundle.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${t.slug} bundle`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally {
      setBusyId(null);
    }
  };

  const pwaBuilderLink = (t) => {
    const origin = t.custom_domain
      ? (t.custom_domain.startsWith("http") ? t.custom_domain : `https://${t.custom_domain}`)
      : `${window.location.origin}/t/${t.slug}`;
    return `https://www.pwabuilder.com/reportcard?site=${encodeURIComponent(origin)}`;
  };

  const storefrontUrl = (t) => {
    if (t.custom_domain) {
      return t.custom_domain.startsWith("http") ? t.custom_domain : `https://${t.custom_domain}`;
    }
    return `${window.location.origin}/t/${t.slug}/`;
  };

  /** Apex origin where Android/Apple actually fetch `.well-known/...`. The
   *  TWA spec only cares about the host — `/t/{slug}` is never part of
   *  the verification URL. */
  const verificationApex = (t) => {
    if (t.custom_domain) {
      const root = t.custom_domain.startsWith("http") ? t.custom_domain : `https://${t.custom_domain}`;
      return root.replace(/\/+$/, "");
    }
    return window.location.origin;
  };

  const isSharedHost = (t) => !t.custom_domain;

  return (
    <div data-testid="super-pwa-page">
      <div className="overline mb-2">Platform</div>
      <h1 className="font-display text-4xl mb-2">Mobile App Bundles</h1>
      <p className="text-sm text-[hsl(var(--kb-muted))] mb-6 max-w-2xl">
        Easiest path: open the tenant's storefront in{" "}
        <a href="https://www.pwabuilder.com" target="_blank" rel="noopener noreferrer" className="underline">PWABuilder.com</a>{" "}— it generates both an Android AAB and an iOS package. Once the tenant ships to Play Store, paste the <code>assetlinks.json</code> PWABuilder gives them using the <b>Edit assetlinks</b> button on each row.{" "}
        <b>Important:</b> saving here writes to the database but the live file at <code>/.well-known/assetlinks.json</code> is baked at deploy time — <b>redeploy the platform</b> to publish your changes to Google's TWA verifier.
      </p>

      <div className="relative max-w-md mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--kb-muted))]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by brand, slug or domain…"
          className="kb-input !pl-8"
          data-testid="super-pwa-search"
        />
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--kb-surface-alt))] text-left">
            <tr>
              <th className="px-4 py-3 overline">Brand</th>
              <th className="px-4 py-3 overline">Slug</th>
              <th className="px-4 py-3 overline">Status</th>
              <th className="px-4 py-3 overline">Storefront</th>
              <th className="px-4 py-3 overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[hsl(var(--kb-muted))]">Loading tenants…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[hsl(var(--kb-muted))]">No tenants match</td></tr>
            )}
            {filtered.map((t) => {
              const url = storefrontUrl(t);
              return (
                <tr key={t.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`pwa-row-${t.slug}`}>
                  <td className="px-4 py-3 font-medium">{t.name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider">
                    <span className={
                      t.status === "active" ? "text-green-700" :
                      t.status === "trial" ? "text-amber-700" :
                      t.status === "trial_expired" || t.status === "suspended" ? "text-red-700" :
                      "text-[hsl(var(--kb-muted))]"
                    }>{t.status || "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[hsl(var(--kb-primary))] underline text-xs"
                      data-testid={`pwa-storefront-${t.slug}`}
                    >
                      <ExternalLink size={11} /> {url.replace(/^https?:\/\//, "")}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setEditing(t)}
                        className="kb-btn !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
                        data-testid={`pwa-edit-assetlinks-${t.slug}`}
                      >
                        <FileJson size={12} /> Edit assetlinks
                      </button>
                      <a
                        href={pwaBuilderLink(t)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kb-btn-outline !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
                        data-testid={`pwa-pwabuilder-${t.slug}`}
                      >
                        <Smartphone size={12} />
                        PWABuilder
                      </a>
                      <button
                        onClick={() => download(t)}
                        disabled={busyId === t.id}
                        className="kb-btn-outline !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
                        data-testid={`pwa-download-${t.slug}`}
                      >
                        <Download size={12} />
                        {busyId === t.id ? "Building…" : "Bundle ZIP"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 text-xs text-[hsl(var(--kb-muted))] max-w-2xl space-y-1">
        <p><b>Edit assetlinks</b> — paste the <code>assetlinks.json</code> / <code>apple-app-site-association</code> PWABuilder gives the tenant after they build the package. Served immediately at <code>/.well-known/...</code>.</p>
        <p><b>PWABuilder</b> — opens the official Microsoft PWABuilder report card prefilled with the tenant's storefront URL. Generates both Android (AAB) and iOS packages.</p>
        <p><b>Bundle ZIP</b> (advanced) — Bubblewrap CLI configs + README, for tenants who want to build the Android TWA locally.</p>
      </div>

      {editing && (
        <PwaEditorModal
          tenant={editing}
          servedOrigin={verificationApex(editing)}
          isSharedHost={isSharedHost(editing)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-tenant assetlinks / AASA editor modal
// ---------------------------------------------------------------------------

function PwaEditorModal({ tenant, servedOrigin, isSharedHost, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ assetlinks_json: "", apple_aasa_json: "" });
  const [drift, setDrift] = useState({ checking: false, hasDrift: false, msg: "" });

  // After every save (and on initial open), compare the DB state to what's
  // actually live at the apex /.well-known/... URL. If they differ, the
  // production frontend container has stale baked content — surface a
  // very loud warning so the user knows a redeploy is required.
  const checkDrift = async () => {
    setDrift({ checking: true, hasDrift: false, msg: "" });
    try {
      const [{ data: dbState }, liveRes] = await Promise.all([
        api.get(`/superadmin/tenants/${tenant.id}/pwa`),
        fetch(`${servedOrigin}/.well-known/assetlinks.json?_=${Date.now()}`, {
          headers: { Accept: "application/json" },
        }).then((r) => r.text()).catch(() => ""),
      ]);
      const dbAssetlinks = (dbState.assetlinks_json || "").trim();
      if (!dbAssetlinks) {
        setDrift({ checking: false, hasDrift: false, msg: "No saved assetlinks yet." });
        return;
      }
      // Both serve aggregated arrays — extract THIS tenant's package entries
      // from each, then diff.
      const extractPkgs = (raw) => {
        try {
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed.map((s) => ({
            pkg: s?.target?.package_name,
            shas: (s?.target?.sha256_cert_fingerprints || []).slice().sort(),
          })).filter((x) => x.pkg);
        } catch { return []; }
      };
      const dbPkgs = extractPkgs(dbAssetlinks);
      const livePkgs = extractPkgs(liveRes);
      const liveByPkg = new Map(livePkgs.map((x) => [x.pkg, JSON.stringify(x.shas)]));
      const missingInLive = dbPkgs.filter(
        (x) => liveByPkg.get(x.pkg) !== JSON.stringify(x.shas)
      );
      if (missingInLive.length > 0) {
        const names = missingInLive.map((x) => x.pkg).join(", ");
        setDrift({
          checking: false,
          hasDrift: true,
          msg: `Your saved entries (${names}) are NOT live at ${servedOrigin}/.well-known/assetlinks.json yet. The production frontend has the old baked version — redeploy the platform to publish.`,
        });
      } else {
        setDrift({ checking: false, hasDrift: false, msg: "Live — your saved entries are being served at the apex URL." });
      }
    } catch (e) {
      setDrift({ checking: false, hasDrift: false, msg: `Could not check: ${e.message}` });
    }
  };

  useEffect(() => {
    let alive = true;
    api.get(`/superadmin/tenants/${tenant.id}/pwa`)
      .then(({ data }) => {
        if (!alive) return;
        setForm({
          assetlinks_json: data.assetlinks_json || "",
          apple_aasa_json: data.apple_aasa_json || "",
        });
        // Auto-check drift on open
        checkDrift();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load tenant PWA config"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/tenants/${tenant.id}/pwa`, form);
      toast.success(`Saved for ${tenant.slug}. Redeploy the platform to publish to ${servedOrigin}/.well-known/...`);
      await checkDrift();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      data-testid="super-pwa-edit-modal"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-3xl w-full border border-[hsl(var(--kb-border))] my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[hsl(var(--kb-border))] flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--kb-muted))]">Editing PWA verification files for</div>
            <div className="font-medium truncate">{tenant.name} <span className="text-[hsl(var(--kb-muted))] font-mono text-xs">({tenant.slug})</span></div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[hsl(var(--kb-surface-alt))]"
            data-testid="super-pwa-edit-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="py-12 text-center text-[hsl(var(--kb-muted))]">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…
            </div>
          ) : (
            <>
              {/* Drift banner — fires when the DB state and the live apex
                  file disagree, which is exactly the "I saved but it's
                  still old" production scenario. */}
              {drift.hasDrift && (
                <div className="border border-red-300 bg-red-50 p-3 text-xs text-red-900 flex gap-2" data-testid="super-pwa-drift-banner">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-700" />
                  <div className="flex-1">
                    <b>⚠ Pending redeploy</b>
                    <div className="mt-1">{drift.msg}</div>
                    <div className="mt-2 text-[11px]">
                      Open the platform's Deploy panel and click <b>Redeploy</b>. The build will re-bake <code>/.well-known/assetlinks.json</code> from the current DB state. After that, click <b>Re-check</b> below.
                    </div>
                    <button
                      onClick={checkDrift}
                      disabled={drift.checking}
                      className="mt-2 px-2 py-1 text-[11px] border border-red-300 bg-white hover:bg-red-100 inline-flex items-center gap-1"
                      data-testid="super-pwa-drift-recheck"
                    >
                      {drift.checking ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                      Re-check live
                    </button>
                  </div>
                </div>
              )}
              {!drift.hasDrift && drift.msg && !drift.checking && (
                <div className="border border-green-200 bg-green-50 p-2.5 text-xs text-green-800 flex gap-2" data-testid="super-pwa-drift-ok">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-green-700" />
                  <div className="flex-1">{drift.msg}</div>
                  <button
                    onClick={checkDrift}
                    className="px-2 py-1 text-[11px] border border-green-300 bg-white hover:bg-green-100"
                    data-testid="super-pwa-drift-recheck-ok"
                  >Re-check</button>
                </div>
              )}
              {isSharedHost && (
                <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3 text-xs flex gap-2" data-testid="super-shared-host-warning">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--kb-muted))]" />
                  <div>
                    <b>Shared platform host.</b> This tenant doesn't have a custom domain, so their <code>/.well-known/assetlinks.json</code> entries are merged into the single file at <code>{servedOrigin}/.well-known/assetlinks.json</code> along with every other shared-host tenant. Google's spec supports this — multiple statements in one file is exactly how Play Store verifies several TWAs that share a host.
                    <div className="mt-2 text-[11px]">
                      <b>Production note:</b> Saves go into the database immediately, but the live <code>/.well-known/...</code> file is baked at deploy time. After saving here, redeploy the platform to publish.
                    </div>
                  </div>
                </div>
              )}
              <WellKnownEditor
                label="assetlinks.json"
                hint="Paste the file PWABuilder generates when this tenant builds the Android app. Required by Google so the Play Store install banner verifies the domain owns the package."
                value={form.assetlinks_json}
                onChange={(v) => setForm({ ...form, assetlinks_json: v })}
                servedAt={`${servedOrigin}/.well-known/assetlinks.json`}
                testIdPrefix="super-assetlinks"
              />
              <WellKnownEditor
                label="apple-app-site-association"
                hint="Paste the AASA file PWABuilder generates with the iOS package. Required for iOS universal links."
                value={form.apple_aasa_json}
                onChange={(v) => setForm({ ...form, apple_aasa_json: v })}
                servedAt={`${servedOrigin}/.well-known/apple-app-site-association`}
                testIdPrefix="super-aasa"
              />
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[hsl(var(--kb-border))] flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="kb-btn-outline !py-1.5 !px-3 !text-xs"
            data-testid="super-pwa-edit-cancel"
          >Cancel</button>
          <button
            onClick={save}
            disabled={saving || loading}
            className="kb-btn !py-1.5 !px-3 !text-xs"
            data-testid="super-pwa-edit-save"
          >{saving ? "Saving…" : "Save (requires redeploy)"}</button>
        </div>
      </div>
    </div>
  );
}
